import { useMutation, useQuery } from '@apollo/client';
import { Add } from '@mui/icons-material';
import { Box, Button, Stack } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GridRowClickAwayListener } from '../../components/GridRowClickAwayListener/GridRowClickAwayListener';
import { useGridValidationStyling } from '../../components/IntakeDataGrid/IntakeDataGrid.hooks';
import { IntakeDrawer } from '../../components/IntakeDrawer/IntakeDrawer';
import { IntakeDrawerContent } from '../../components/IntakeDrawer/IntakeDrawerContent';
import { IntakeDrawerTitle } from '../../components/IntakeDrawer/IntakeDrawerTitle';
import { supplierContactSelectionArraySchema } from '../../components/SupplierContactForm/SupplierContactForm.schema';
import {
  IntakeRequestSupplierSelectionGridField,
  useSupplierGridHandlers,
} from '../../hooks/IntakeSupplierSelection.dataGrid';
import { SvbrgChatSessionVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { ChatMessageProps } from '~/common/types/ChatMessage';
import { ChatStep } from '~/components/ChatStep/ChatStep';
import { useChatSession } from '~/modules/Module.hooks';
import { IntakeDataGrid } from '~/routes/IntakeDetail/components/IntakeDataGrid/IntakeDataGrid';
import { SupplierContactFormRoot } from '~/routes/IntakeDetail/components/SupplierContactFormRoot/SupplierContactFormRoot.tsx';

import { useRequestSupplierSelectionGridConfig } from './IntakeRequestSupplierSelectionStep.dataGrid';
import { useNextMutation, useSupplierData } from './IntakeRequestSupplierSelectionStep.hooks';
import { INTAKE_SUPPLIER_SELECTION_NEXT_MUTATION } from './IntakeRequestSupplierSelectionStep.mutation.gql';
import { INTAKE_SUPPLIER_SELECTION } from './IntakeRequestSupplierSelectionStep.query.gql';

export const IntakeRequestSupplierSelectionStep = ({ role, chatSessionCompleted, createdBy }: ChatMessageProps) => {
  const { t: tIntake } = useTranslation(I18nNamespace.intake);
  const { t } = useTranslation(I18nNamespace.intake, { keyPrefix: 'IntakeRequestSupplierSelectionStep' });
  const session = useChatSession();
  const { data, loading } = useQuery(INTAKE_SUPPLIER_SELECTION, { variables: session, fetchPolicy: 'no-cache' });
  const [next, { loading: nextLoading }] = useMutation(INTAKE_SUPPLIER_SELECTION_NEXT_MUTATION);
  const isLoading = loading || nextLoading;
  const apiRef = useGridApiRef();

  const { handleNext, handleSkip } = useNextMutation({ apiRef, session, next, isLoading });

  const { getRowClassName, getGridClassName } = useGridValidationStyling(apiRef, supplierContactSelectionArraySchema);

  const {
    currentSelectedRowId,
    handleAddRow,
    handleRowDelete,
    handleCloseDrawer,
    handleSupplierChange,
    selectedSupplier,
    setRowSelectionModel,
    validate,
  } = useSupplierGridHandlers(apiRef, supplierContactSelectionArraySchema);

  const isDisabled = chatSessionCompleted || isLoading;

  const { suppliers, quotedSupplierId } = useSupplierData(data);
  const config = useRequestSupplierSelectionGridConfig({
    onRowDelete: handleRowDelete,
    disabled: isDisabled,
    // DLR flow specific variant to include the source column to indicate how the supplier was identified
    includeSource: data?.sevenbridges.chatSession.variant === SvbrgChatSessionVariant.REQUEST_DLR,
  });

  const rowSelectionModel = currentSelectedRowId ? [currentSelectedRowId] : [];

  const otherSuppliers = useMemo(() => {
    const result: string[] = [];
    if (currentSelectedRowId) {
      apiRef?.current.getRowModels().forEach((row, key) => {
        if (key !== currentSelectedRowId) {
          result.push(row.supplier.internalId);
        }
      });
    }
    if (quotedSupplierId) {
      result.push(quotedSupplierId);
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedRowId, quotedSupplierId]);

  // enforce initial selection state and avoid error coloring
  // temporary fix until proper table validation styling is in place
  useEffect(() => {
    setRowSelectionModel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers]);

  return (
    <ChatStep
      title={tIntake('IntakeChatStep.assistant')}
      role={role}
      createdBy={createdBy}
      message={t('message')}
      footer={
        <Stack direction="row" spacing={2}>
          <Button disabled={isDisabled} color="primary" variant="contained" onClick={handleNext}>
            {t('button.next')}
          </Button>
          <Button disabled={isDisabled} color="primary" variant="outlined" onClick={handleSkip}>
            {t('button.skip')}
          </Button>
        </Stack>
      }
    >
      {!loading && (
        <>
          <GridRowClickAwayListener onClickAway={handleCloseDrawer}>
            <IntakeDataGrid
              {...config}
              apiRef={apiRef}
              rows={suppliers}
              initialState={{
                pinnedColumns: { right: [IntakeRequestSupplierSelectionGridField.DELETE_ACTION] },
              }}
              editMode="row"
              columns={config.columns}
              onRowSelectionModelChange={setRowSelectionModel}
              rowSelectionModel={rowSelectionModel}
              getRowClassName={({ id, row }) => {
                return id !== currentSelectedRowId || validate ? getRowClassName(row) : '';
              }}
              className={getGridClassName()}
            />
            <IntakeDrawer isOpen={!!selectedSupplier} handleCloseDrawer={handleCloseDrawer}>
              <IntakeDrawerTitle title={t('editSupplier')} handleCloseDrawer={handleCloseDrawer} />
              <IntakeDrawerContent>
                {selectedSupplier && (
                  <SupplierContactFormRoot
                    // use key to make sure a new form is rendered when supplier changes
                    key={selectedSupplier.id}
                    disabled={isDisabled}
                    supplier={selectedSupplier}
                    suppliersOmit={otherSuppliers}
                    validate={validate}
                    onChange={handleSupplierChange}
                  />
                )}
              </IntakeDrawerContent>
            </IntakeDrawer>
          </GridRowClickAwayListener>
          <Box
            sx={{
              mt: 3,
            }}
          >
            <Button
              disabled={isDisabled}
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                handleAddRow();
              }}
              startIcon={<Add />}
            >
              {t('button.addSupplier')}
            </Button>
          </Box>
        </>
      )}
    </ChatStep>
  );
};
